import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useGetList, useGetOne, RecordContextProvider, useNotify, useRefresh, useUpdate, Create, Edit, SimpleForm, Toolbar, SaveButton, useCreate, useDataProvider, useGetIdentity } from 'react-admin';
import { Tree } from 'primereact/tree';
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import UndoIcon from '@mui/icons-material/Undo';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import CodeIcon from '@mui/icons-material/Code';
import CloseIcon from '@mui/icons-material/Close';
import { AIPromptsCommandPanel } from './AIPromptsCommandPanel';
import {
  entityTypes,
  outputTypes,
  outputTypesForCustomSessionNotes,
} from './AIPromptsList';
import { AIPromptsEditForm } from './AIPromptsEdit';
import { BSON } from 'realm-web';

const LeafNodeContent = ({ 
  label, 
  isInherited, 
  isArchived,
  prompt,
  frameworkId,
  onSelect 
}: { 
  label: string, 
  isInherited: boolean, 
  isArchived: boolean,
  prompt: any,
  frameworkId?: string | null,
  onSelect: (p: any, inherited: boolean, archived: boolean, frameworkId?: string | null) => void 
}) => {
  const hasInstruction = prompt?.instruction;

  return (
    <Box 
      onClick={() => onSelect(prompt, isInherited, isArchived, frameworkId)}
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        width: '100%', 
        py: 0.5,
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {hasInstruction ? (
          <CheckCircleIcon sx={{ color: 'success.main', fontSize: '1rem', mr: 1, flexShrink: 0 }} />
        ) : (
          <WarningIcon sx={{ color: 'warning.main', fontSize: '1rem', mr: 1, flexShrink: 0 }} />
        )}
        <Typography 
          variant="subtitle2" 
          sx={{ 
            fontWeight: isInherited ? 400 : 600,
            color: isInherited ? 'text.secondary' : 'text.primary',
            fontStyle: isInherited ? 'italic' : 'normal'
          }}
        >
          {label} {isInherited ? '(Inherited)' : ''} {isArchived ? '(Archived)' : ''}
        </Typography>
      </Box>
    </Box>
  );
};

const AIPromptsTreeView = () => {
  const [selectedPrompt, setSelectedRecord] = useState<any>(null);
  const [isInherited, setIsInherited] = useState(false);
  const [isArchived, setIsArchived] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState<any>({});
  const [confirmInheritOpen, setConfirmInheritOpen] = useState(false);
  const [leftPanelWidth, setLeftPanelWidth] = useState(25); // Default 25% width
  const [isDragging, setIsDragging] = useState(false);
  const [rawContentsOpen, setRawContentsOpen] = useState(false);
  const [confirmEditGeneralOpen, setConfirmEditGeneralOpen] = useState(false);
  const [targetFrameworkId, setTargetFrameworkId] = useState<string | null>(null);
  const [confirmOverrideOpen, setConfirmOverrideOpen] = useState(false);
  const [isCreatingOverride, setIsCreatingOverride] = useState(false);
  const [confirmRestoreOpen, setConfirmRestoreOpen] = useState(false);
  const [isRestoringOverride, setIsRestoringOverride] = useState(false);

  const notify = useNotify();
  const refresh = useRefresh();
  const [update] = useUpdate();
  const dataProvider = useDataProvider();
  const { data: identity } = useGetIdentity();
  const userId = identity?.id?.toString();

  // Fetch prompts (including archived ones if possible, but standard useGetList might not return them by default)
  // We'll assume for now we only see active ones unless we add a custom fetcher or meta param
  const { data: prompts, isLoading: loadingPrompts, refetch: refetchPrompts } = useGetList('aiPrompts', {
    pagination: { page: 1, perPage: 2000 },
    sort: { field: 'entityType', order: 'ASC' },
    filter: { _includeDeleted: true } // Assuming backend supports this or similar
  });

  const { data: frameworks, isLoading: loadingFrameworks } = useGetList('therapistSessionNotesFrameworks', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' }
  });

  const handleSelect = (prompt: any, inherited: boolean, archived: boolean, frameworkId?: string | null) => {
    // Reset edit mode when switching prompts to ensure form re-initializes
    setEditMode(false);
    setSelectedRecord(prompt);
    setIsInherited(inherited);
    setIsArchived(archived);
    // Store the target framework ID for override creation
    setTargetFrameworkId(inherited ? (frameworkId || null) : null);
  };

  // Set edit mode based on inheritance status after prompt is selected
  useEffect(() => {
    if (selectedPrompt) {
      // Default to edit mode for existing overrides/first-class prompts, view mode for inherited
      setEditMode(!isInherited);
    }
  }, [selectedPrompt, isInherited, isArchived, editMode]);

  // Track prompts list changes after refresh
  useEffect(() => {
    // This effect can be used for tracking if needed
  }, [prompts, selectedPrompt]);

  const handleSaveSuccess = useCallback((data: any) => {
    // Update the selected prompt with the saved data
    setSelectedRecord(data);
    // Refresh the prompts list to get the latest data
    refresh();
    // Switch back to view mode after save
    setEditMode(false);
    notify('Prompt saved successfully', { type: 'success' });
  }, [refresh, notify]);

  const handleInherit = () => {
    if (!selectedPrompt) return;
    
    update(
      'aiPrompts',
      { id: selectedPrompt.id, data: { deletedAt: new Date() } },
      {
        onSuccess: () => {
          notify('Prompt archived. Now inheriting from general.');
          refresh();
          setConfirmInheritOpen(false);
          setSelectedRecord(null);
        }
      }
    );
  };

  const handleCreateOverride = useCallback(async () => {
    if (!selectedPrompt || !targetFrameworkId) return;

    setIsCreatingOverride(true);
    
    try {
      // Clone the General prompt data
      const overrideData = {
        ...selectedPrompt,
        id: new BSON.ObjectId().toHexString(), // Generate new ID
        subType: targetFrameworkId, // Set to the framework we're overriding for
        createdAt: new Date(),
        createdBy: userId,
        // Remove any fields that shouldn't be copied
        updatedAt: undefined,
        updatedBy: undefined,
      };

      // Create the override prompt using dataProvider
      const result = await dataProvider.create('aiPrompts', {
        data: overrideData,
      });

      // Refresh the prompts list
      await refresh();

      // Switch to edit mode with the newly created prompt
      setSelectedRecord(result.data);
      setIsInherited(false);
      setIsArchived(false);
      setEditMode(true);
      setConfirmOverrideOpen(false);
      setIsCreatingOverride(false);
      
      notify('Override prompt created successfully', { type: 'success' });
    } catch (error: any) {
      setIsCreatingOverride(false);
      notify('Failed to create override prompt', { type: 'error' });
      console.error('Error creating override:', error);
    }
  }, [selectedPrompt, targetFrameworkId, dataProvider, refresh, notify, userId]);

  const handleRestoreOverride = useCallback(async () => {
    if (!selectedPrompt) return;
    
    // Ensure we're actually restoring an archived prompt
    if (!isArchived || !selectedPrompt.deletedAt) {
      notify('This prompt is not archived and cannot be restored', { type: 'warning' });
      return;
    }

    setIsRestoringOverride(true);
    
    try {
      // Unarchive the existing prompt by setting deletedAt to null
      const result = await dataProvider.update('aiPrompts', {
        id: selectedPrompt.id,
        data: { deletedAt: null },
        previousData: selectedPrompt,
      });

      // Refresh the prompts list (fire-and-forget, will update via useGetList hook)
      refresh();
      
      // Switch to edit mode with the restored prompt
      // Use result.data directly - it has the updated deletedAt: null
      // The tree will rebuild when prompts updates via useGetList hook
      const restoredPrompt = result.data;
      setSelectedRecord(restoredPrompt);
      setIsInherited(false);
      setIsArchived(false);
      setEditMode(true);
      setConfirmRestoreOpen(false);
      setIsRestoringOverride(false);
      
      notify('Override prompt restored successfully', { type: 'success' });
    } catch (error: any) {
      setIsRestoringOverride(false);
      notify('Failed to restore override prompt', { type: 'error' });
      console.error('Error restoring override:', error);
    }
  }, [selectedPrompt, dataProvider, refresh, notify, prompts, refetchPrompts, isArchived]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    
    const container = document.querySelector('[data-resizable-container]') as HTMLElement;
    if (!container) return;
    
    const containerRect = container.getBoundingClientRect();
    const newWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;
    
    // Constrain between 20% and 50%
    const constrainedWidth = Math.max(20, Math.min(50, newWidth));
    setLeftPanelWidth(constrainedWidth);
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const treeData = useMemo(() => {
    if (!prompts) return [];

    const allFrameworks = [
      { id: null, name: 'General' },
      { id: 'custom' as const, name: 'Custom' },
      ...(frameworks || []),
    ];

    const tree = Object.entries(entityTypes).map(([entityId, entityName]) => {
      const entityPrompts = prompts.filter(p => p.entityType === entityId);
      
      const frameworkNodes = allFrameworks.map(fw => {
        const fwId = fw.id;
        const fwName = fw.name;
        
        const outputTypesForFramework =
          fwId === 'custom'
            ? outputTypesForCustomSessionNotes
            : outputTypes;

        const outputNodes = outputTypesForFramework.map(ot => {
          // For "General" framework (fwId === null), look for prompts with subType === null
          // For other frameworks, look for prompts with subType === fwId, or fall back to General (subType === null)
          const specificPrompt = fwId === null 
            ? entityPrompts.find(p => (p.subType === null || p.subType === undefined) && p.outputType === ot && !p.deletedAt)
            : entityPrompts.find(p => p.subType === fwId && p.outputType === ot && !p.deletedAt);
          
          const archivedPrompt = fwId === null
            ? entityPrompts.find(p => (p.subType === null || p.subType === undefined) && p.outputType === ot && !!p.deletedAt)
            : entityPrompts.find(p => p.subType === fwId && p.outputType === ot && !!p.deletedAt);
          
          // For non-General frameworks, also check for General fallback
          const generalPrompt = fwId !== null 
            ? entityPrompts.find(p => (p.subType === null || p.subType === undefined) && p.outputType === ot && !p.deletedAt)
            : null;

          const activePrompt = specificPrompt || generalPrompt;
          const inherited = !specificPrompt && fwId !== null && !!generalPrompt;
          const archived = !!archivedPrompt && !specificPrompt;

          if (!activePrompt && !archivedPrompt) return null;

          // If archived, use the archived prompt; otherwise use the active prompt
          // This ensures we select the correct prompt when clicking on archived nodes
          const nodeData = archived ? archivedPrompt : activePrompt;

          return {
            key: `${entityId}-${fwId || 'general'}-${ot}`,
            label: ot,
            data: nodeData,
            leaf: true,
            isInherited: inherited,
            isArchived: archived && !specificPrompt,
            level: 2, // Output type level
            frameworkId: fwId, // Store the framework ID for override creation
          };
        }).filter(Boolean);

        return {
          key: `${entityId}-${fwId || 'general'}`,
          label: fwName,
          children: outputNodes,
          selectable: false,
          level: 1, // Framework level
        };
      }).filter(node => node.children && node.children.length > 0);

      return {
        key: entityId,
        label: entityName as string,
        children: frameworkNodes,
        selectable: false,
        level: 0, // Entity type level
      };
    }).filter(node => node.children && node.children.length > 0);

    return tree;
  }, [prompts, frameworks]);

  const nodeTemplate = (node: any) => {
    if (!node.leaf) {
      const level = node.level || 0;
      const isEntityType = level === 0;
      const isFramework = level === 1;
      
      return (
        <Box
          sx={{
            py: 1,
            px: isEntityType ? 0 : isFramework ? 1 : 3,
            my: 0.5,
            borderLeft: isFramework ? '3px solid' : 'none',
            borderColor: isFramework ? 'primary.main' : 'transparent',
            borderRadius: 1,
          }}
        >
          <Typography 
            variant={isEntityType ? 'h6' : 'subtitle1'} 
            sx={{ 
              fontWeight: isEntityType ? 700 : 600,
              color: isEntityType ? 'text.primary' : 'text.secondary',
            }}
          >
            {node.label}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ pl: 4, py: 0.25 }}>
        <LeafNodeContent 
          label={node.label}
          prompt={node.data}
          isInherited={node.isInherited}
          isArchived={node.isArchived}
          frameworkId={node.frameworkId}
          onSelect={handleSelect}
        />
      </Box>
    );
  };

  if (loadingPrompts || loadingFrameworks) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Loading agent prompts...</Typography>
      </Box>
    );
  }

  return (
    <Box 
      data-resizable-container
      sx={{ display: 'flex', mt: 2, height: 'calc(100vh - 150px)', position: 'relative' }}
    >
      {/* Left Panel: Tree */}
      <Paper sx={{ width: `${leftPanelWidth}%`, overflow: 'auto', p: 2, flexShrink: 0 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Hierarchical View</Typography>
        <Box
          sx={{
            '& .p-tree-container': {
              padding: 0,
            },
            '& .p-treenode': {
              padding: 0,
            },
            '& .p-treenode-content': {
              padding: 0,
              margin: 0,
            },
            '& .p-treenode-children': {
              padding: 0,
              margin: 0,
            },
            // Add left padding to framework-level nodes (children of entity type nodes)
            '& .p-treenode > .p-treenode-children > .p-treenode > .p-treenode-content': {
              paddingLeft: '24px !important',
            },
          }}
        >
          <Tree
            value={treeData}
            expandedKeys={expandedNodes}
            onToggle={(e) => setExpandedNodes(e.value)}
            nodeTemplate={nodeTemplate}
            className="w-full"
            style={{ background: 'transparent', border: 'none' }}
          />
        </Box>
      </Paper>

      {/* Resizable Divider */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          width: '4px',
          cursor: 'col-resize',
          bgcolor: 'divider',
          flexShrink: 0,
          '&:hover': {
            bgcolor: 'primary.main',
          },
          transition: 'background-color 0.2s',
        }}
      />

      {/* Right Panel: Details/Edit */}
      <Paper sx={{ width: `${100 - leftPanelWidth}%`, overflow: 'auto', p: 3, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        {!selectedPrompt ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'text.disabled' }}>
            <Typography>Select a prompt from the tree to view or edit details</Typography>
          </Box>
        ) : (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {selectedPrompt.outputType}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  {entityTypes[selectedPrompt.entityType as keyof typeof entityTypes]} /{' '}
                  {selectedPrompt.subType
                    ? selectedPrompt.subType === 'custom'
                      ? 'Custom'
                      : frameworks?.find(f => f.id === selectedPrompt.subType)
                          ?.name || selectedPrompt.subType
                    : 'General'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  variant="outlined" 
                  color="secondary" 
                  startIcon={<CodeIcon />}
                  onClick={() => setRawContentsOpen(true)}
                >
                  View Raw Contents
                </Button>
                {editMode && !isInherited && !isArchived && selectedPrompt?.subType != null && (
                  <Button 
                    variant="outlined" 
                    color="warning" 
                    startIcon={<UndoIcon />}
                    onClick={() => setConfirmInheritOpen(true)}
                  >
                    Inherit from General
                  </Button>
                )}
                {!editMode && isInherited && !isArchived && (
                  <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<CallMergeIcon />}
                    onClick={() => setConfirmOverrideOpen(true)}
                  >
                    Override
                  </Button>
                )}
                {!editMode && isArchived && (
                  <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<CallMergeIcon />}
                    onClick={() => {
                      setConfirmRestoreOpen(true);
                    }}
                  >
                    Restore Override
                  </Button>
                )}
                {editMode && (isInherited || isArchived) && (
                  <Button variant="text" onClick={() => setEditMode(false)}>
                    Cancel
                  </Button>
                )}
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {isInherited && !editMode && (
              <Alert severity="info" sx={{ mb: 3 }}>
                This prompt is currently inherited from the <strong>General</strong> configuration. Click <strong>Override</strong> to create a specialized version for this framework.
              </Alert>
            )}

            {isArchived && !editMode && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                A specific prompt for this framework was archived. It is currently inheriting from <strong>General</strong>. Click <strong>Override</strong> to restore and edit it.
              </Alert>
            )}

            <Box sx={{ flexGrow: 1 }}>
              {editMode ? (
                <RecordContextProvider 
                  value={selectedPrompt} 
                  key={`edit-${selectedPrompt?.id || 'new'}-${selectedPrompt?.outputType}`}
                >
                  <Edit 
                    resource="aiPrompts" 
                    id={selectedPrompt.id} 
                    redirect={false}
                    mutationMode="pessimistic"
                    transform={(data) => ({
                      ...data,
                      deletedAt: null, // Ensure unarchiving if it was archived
                      updatedAt: new Date(),
                    })}
                    mutationOptions={{ onSuccess: handleSaveSuccess }}
                    key={`edit-${selectedPrompt.id}`}
                  >
                    <AIPromptsEditForm />
                  </Edit>
                </RecordContextProvider>
              ) : (
                <RecordContextProvider value={selectedPrompt} key={`view-${selectedPrompt?.id || 'none'}`}>
                  <AIPromptsCommandPanel isInherited={isInherited} />
                </RecordContextProvider>
              )}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Inherit Confirmation Dialog */}
      <Dialog open={confirmInheritOpen} onClose={() => setConfirmInheritOpen(false)}>
        <DialogTitle>Confirm Inheritance</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to inherit from the General configuration? This will archive (delete) the current specific prompt for this framework.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmInheritOpen(false)}>Cancel</Button>
          <Button onClick={handleInherit} color="warning" variant="contained">Confirm & Inherit</Button>
        </DialogActions>
      </Dialog>

      {/* Edit General Framework Confirmation Dialog */}
      <Dialog open={confirmEditGeneralOpen} onClose={() => setConfirmEditGeneralOpen(false)}>
        <DialogTitle>Edit General Framework</DialogTitle>
        <DialogContent>
          <Typography>
            You are about to edit the <strong>General</strong> framework prompt. This will affect all frameworks that inherit from General. Are you sure you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmEditGeneralOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              // Find the General framework prompt and switch to it
              const generalPrompt = prompts?.find(
                p => p.entityType === selectedPrompt?.entityType && 
                     p.outputType === selectedPrompt?.outputType && 
                     (p.subType === null || p.subType === undefined) &&
                     !p.deletedAt
              );
              if (generalPrompt) {
                setSelectedRecord(generalPrompt);
                setIsInherited(false);
                setIsArchived(false);
                setEditMode(true);
                setConfirmEditGeneralOpen(false);
              }
            }} 
            color="primary" 
            variant="contained"
          >
            Edit General Framework
          </Button>
        </DialogActions>
      </Dialog>

      {/* Override Confirmation Dialog */}
      <Dialog 
        open={confirmOverrideOpen} 
        onClose={() => !isCreatingOverride && setConfirmOverrideOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Override Prompt</DialogTitle>
        <DialogContent>
          {isCreatingOverride ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Creating override prompt...
              </Typography>
              <CircularProgress />
            </Box>
          ) : (
            <Typography>
              You are about to create an override prompt for <strong>{targetFrameworkId === 'custom' ? 'Custom' : frameworks?.find(f => f.id === targetFrameworkId)?.name || 'this framework'}</strong>. 
              All content from the <strong>General</strong> configuration will be duplicated, and you will be able to edit it for this specific framework.
              <br /><br />
              Are you sure you want to proceed?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmOverrideOpen(false)} 
            disabled={isCreatingOverride}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateOverride} 
            color="primary" 
            variant="contained"
            disabled={isCreatingOverride}
          >
            {isCreatingOverride ? 'Creating...' : 'Create Override'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore Override Confirmation Dialog */}
      <Dialog 
        open={confirmRestoreOpen} 
        onClose={() => !isRestoringOverride && setConfirmRestoreOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Restore Override Prompt</DialogTitle>
        <DialogContent>
          {isRestoringOverride ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Restoring override prompt...
              </Typography>
              <CircularProgress />
            </Box>
          ) : (
            <Typography>
              You are about to restore an archived override prompt for <strong>{selectedPrompt?.subType === 'custom' ? 'Custom' : frameworks?.find(f => f.id === selectedPrompt?.subType)?.name || 'this framework'}</strong>. 
              This will unarchive the existing prompt and allow you to edit it.
              <br /><br />
              Are you sure you want to proceed?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmRestoreOpen(false)} 
            disabled={isRestoringOverride}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleRestoreOverride} 
            color="primary" 
            variant="contained"
            disabled={isRestoringOverride}
          >
            {isRestoringOverride ? 'Restoring...' : 'Restore Override'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Raw Contents Dialog */}
      <Dialog 
        open={rawContentsOpen} 
        onClose={() => setRawContentsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Raw Prompt Contents</Typography>
          <IconButton
            aria-label="close"
            onClick={() => setRawContentsOpen(false)}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {selectedPrompt && (
            <Box sx={{ p: 2 }}>
              <Typography 
                variant="body2" 
                component="pre"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  bgcolor: 'background.default',
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'auto',
                  maxHeight: '70vh',
                }}
              >
                {JSON.stringify(selectedPrompt, null, 2)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRawContentsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AIPromptsTreeView;
